import { FC, useEffect, useState } from 'react';
import Button from '../../../Button';
import {
  IClassAssignment,
  IClassAssignmentQuery,
} from '../../../../models/campusClasses.model';
import { TableColumnsType, Table, Select } from 'antd';
import { TableRowSelection } from 'antd/es/table/interface';
import {
  IGetCurrentSemester,
  ISemester,
  ISemesterCourse,
} from '../../../../models/semesters.model';
import SubmitModal from './modals/SubmitModal';
import useNotification from '../../../../hooks/useNotification';
import useAxios from '../../../../hooks/useAxios';
import { getCurrentSemester } from '../../../../api/semesters.api';
import Loader from '../../../Loader';
import ErrorResponse from '../../../ErrorResponse';
import { Contents } from './Class';
import config from '../../../../lib/config';
import Pagination from '../../../Pagination';
import { useTranslation } from 'react-i18next';

interface IProps {
  data?: IClassAssignment[];
  semesterList?: ISemester[];
  semesterCourseList?: ISemesterCourse[];
  onSearch(params?: IClassAssignmentQuery): void;
  onSubmit(classCode: string, studySrtDate: string, timetableCode: number): void;
  doGetSemesterCourses(semesterCode: string): void;
  setContents?: (contents: Contents) => void;
  eduStepCode?: string;
}

const ClassAssignmentContents: FC<IProps> = ({
  data,
  semesterList,
  semesterCourseList,
  onSearch,
  onSubmit,
  doGetSemesterCourses,
  setContents,
  eduStepCode,
}) => {
  const { t } = useTranslation();
  const { infoNotification } = useNotification();
  const { data: res, isLoading, error, execute } = useAxios<IGetCurrentSemester>();

  const [selectClass, setSelectClass] = useState<string | undefined>();
  const [semesterCourseCode, setSemesterCourseCode] = useState(0);
  const [semesterCode, setSemesterCode] = useState(0);
  const [className, setClassName] = useState('');
  const [visible, setVisible] = useState(false);
  const [page, setPage] = useState(1);

  useEffect(() => {
    execute(getCurrentSemester());
  }, []);

  useEffect(() => {
    async function handleSemesterCode(semesterCode: any) {
      await onSemesterCodeChange(semesterCode);
      await onSearch({ semesterCode });
    }

    if (semesterList && res) {
      if (res.data?.semesterCode) {
        handleSemesterCode(res.data.semesterCode);
      } else {
        onSearch({ semesterCode });
      }
    }
  }, [semesterList, res]);

  const rowSelection: TableRowSelection<IClassAssignment> = {
    type: 'radio',
    onChange: (selectedRowKeys, selectedRows) => {
      setSelectClass(selectedRowKeys[0] as string);
    },
  };

  const onSemesterCodeChange = (value: number) => {
    setSemesterCode(value);
    doGetSemesterCourses(String(value));
  };

  const onSearchEvent = () => {
    onSearch({
      ...(semesterCode !== 0 && { semesterCode }),
      ...(semesterCourseCode !== 0 && { semesterCourseCode }),
      ...(className && { className }),
    });
  };

  const showModal = () => {
    if (!selectClass) return infoNotification(t('student.student.message13'));
    setVisible(true);
  };

  const closeModal = () => setVisible(false);

  const onOk = (startDate: string, timetableCode: number) => {
    if (selectClass) {
      onSubmit(selectClass, startDate, timetableCode);
      closeModal();
    }
  };

  const getSemesterCourseList = () => {
    if (eduStepCode) {
      return semesterCourseList?.filter(item => item.eduStepCode === eduStepCode);
    }
    return semesterCourseList;
  };

  const columns: TableColumnsType<IClassAssignment> = [
    {
      title: t('student.student.column23'),
      dataIndex: 'className',
      key: 'className',
    },
    {
      title: t('student.student.column30'),
      dataIndex: 'courseDetailName',
      key: 'courseDetailName',
    },
    {
      title: t('student.student.column26'),
      dataIndex: 'courseName',
      key: 'courseName',
    },
    {
      title: t('student.student.column27'),
      dataIndex: 'courseSubName',
      key: 'courseSubName',
    },
    {
      title: t('common.column.num'),
      dataIndex: 'studentTotal',
      key: 'studentTotal',
    },
  ];
  
  return (
    <>
      {isLoading && <Loader />}
      <ErrorResponse error={error} />
      <div className="comm-bx__space">
      <div className="comm-btn__area right">
        <div>
          <Select
            className="w-150p"
            value={semesterCode}
            onChange={onSemesterCodeChange}
          >
            <Select.Option value={0}>{t('common.text.all')}</Select.Option>
            {semesterList?.map(({ semesterCode, semesterName }) => (
              <Select.Option key={`semester-${semesterCode}`} value={semesterCode}>
                {semesterName}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div>
          <Select
            className="w-150p"
            value={semesterCourseCode}
            onChange={setSemesterCourseCode}
          >
            <Select.Option value={0}>{t('common.text.all')}</Select.Option>
            {getSemesterCourseList()?.map(({ semesterCourseCode, courseDetailName }) => (
              <Select.Option
                key={semesterCourseCode}
                value={semesterCourseCode}
              >
                {courseDetailName}
              </Select.Option>
            ))}
          </Select>
        </div>
        <div className="w-200p">
          <input
            type="text"
            className="form-control input-text"
            placeholder={t('student.student.placeholder4')}
            value={className}
            onChange={(e) => setClassName(e.target.value)}
            onKeyPress={(e) => {
              if (e.key === 'Enter') onSearchEvent();
            }}
          />
        </div>
        <div className="comm-btn__area">
          <Button
            className="btn-basic small black"
            onClick={onSearchEvent}
            text={t('common.button.search')}
          />
        </div>
        </div>
      </div>
      <Table
        className='comm-table__area mt-0'
        columns={columns}
        dataSource={data}
        rowKey="classCode"
        rowSelection={rowSelection}
        pagination={{
          current: page,
          pageSize: config.miniPageSize,
        }}
      />
      <div className="comm-bx__between">
        <div className="comm-btn__area">
          {
            setContents
              ? (
                  <Button
                    className="btn-basic gray-line"
                    onClick={() => setContents(Contents.default)}
                    text={t('common.button.back')}
                  />
                )
              : null
          }
        </div>
        <div className="common-paging center">
          <Pagination
            count={data?.length || 0}
            page={page}
            setPage={setPage}
            mini
          />
        </div>
        <div className="comm-btn__area right">
          <Button
            className="btn-basic pink"
            onClick={showModal}
            text={t('student.student.button6')}
          />
        </div>
      </div>
      {visible && selectClass && (
        <SubmitModal
          visible={visible}
          classCode={selectClass}
          onOk={onOk}
          onCancel={closeModal}
        />
      )}
    </>
  );
};

export default ClassAssignmentContents;
